import { ORIGIN, post, request } from "~/core/api";

export const fileUrl = (id: string) => `${ORIGIN}/api/v1/agent/files/serve/${encodeURIComponent(id)}`;

/** How the market describes one of a media model's request knobs. */
export type Knob =
	| { type: "enum"; values: (string | number)[] }
	| { type: "range"; min?: number; max?: number }
	| { type: "boolean" };

export interface MediaModel {
	id: string;
	name: string;
	modality: string;
	/** The knobs this model takes beyond prompt and shape; `duration` is its clip lengths. */
	params: Record<string, Knob>;
	/** A speech model's voices; empty when it names none. */
	voices: string[];
	/** Whether resolution or audio moves a clip onto a dearer rate. */
	tiered: boolean;
	credits_per_1000_input: number;
	credits_per_1000_output: number;
	/** The console's default for this kind: what a picker opens on. */
	default?: boolean;
}

/** Knobs the generator already sets through its own fields. */
const FITTED = new Set(["aspect_ratio", "size", "duration"]);

/** The lengths a clip of this model can be; `fallback` when it publishes none. */
export function clipLengths(model: MediaModel | undefined, fallback: number[]): number[] {
	const d = model?.params.duration;
	const values = d?.type === "enum" ? d.values.filter((v): v is number => typeof v === "number") : [];
	return values.length ? [...values].sort((a, b) => a - b) : fallback;
}

/** The knobs a person picks for this model, beside the generator's own fields. */
export function extraKnobs(model: MediaModel | undefined): [string, Knob][] {
	return Object.entries(model?.params ?? {}).filter(([k]) => !FITTED.has(k));
}

/** One knob as a form field. The market flags `seed` as a boolean: it means "takes a seed". */
export function knobField(key: string, knob: Knob) {
	if (knob.type === "enum")
		return { type: typeof knob.values[0] === "number" ? "number" : "string", enum: knob.values };
	if (knob.type === "range") return { type: "number", minimum: knob.min, maximum: knob.max };
	return key === "seed" ? { type: "integer", minimum: 0 } : { type: "boolean" };
}

export const mediaKey = (brand: string) => ["media", brand];
export const mediaOptions = (brand: string) => request<{ models: MediaModel[] }>(`/entities/${brand}/media/options`);

/** A generated file, as `media.generate` reports it. */
export interface GeneratedFile {
	id: string;
	content_type: string;
	name: string;
}

export interface MediaOperation {
	id: string;
	status: "waiting" | "running" | "done" | "failed" | "skipped" | string;
	output: { file?: GeneratedFile } | null;
	error_code: string | null;
}

export interface MediaRequest {
	modality: "image" | "speech" | "video";
	model: string;
	prompt: string;
	preset: string;
	seconds?: number;
	voice?: string;
	params?: Record<string, unknown>;
}

/** Queue one generation. The same request id answers with the same operation. */
export const generateMedia = (brand: string, requestId: string, input: MediaRequest) =>
	post<MediaOperation>(`/entities/${brand}/operations`, {
		capability: "media.generate",
		version: 1,
		idempotency_key: requestId,
		input,
	});
export const readOperation = (id: string) => request<MediaOperation>(`/operations/${id}`);
